import { useState, useEffect } from "react";
import SuccessPopup from "./SuccessPopup";
import EmailModal from "./EmailModal";
import { useOrder } from "./hooks/useOrder";
import { useLedger } from "./hooks/useLedger";
import { useCustomer } from "./hooks/useCustomer";
import { useInvoiceEmail } from "./hooks/useInvoiceEmail";
import { usePrintData } from "./hooks/usePrintData";
import { notifyError, notifySuccess } from "src/assets/toastService";
import { useUser } from "src/business-types/grocery/components/UserContext";
import { errorHandling } from "src/shared/utils/errorHandling";
import { CustomerData } from "src/shared/services/interface";
import LoadingScreen from "src/shared/components/LoadingScreen";

interface DoneProps {
  orderid?: string;
  onFinish: () => void;
  customer?: CustomerData | any;
}

const Done: React.FC<DoneProps> = ({ orderid, onFinish, customer }) => {
  const { order, loading: orderLoading } = useOrder(orderid); // no refetchOrder
  // Prefer the customer id already known from the payment flow so the ledger /
  // customer lookups can run in parallel with the order fetch instead of
  // waiting for it to resolve (avoids an extra render + duplicate API calls).
  // `customer` here is a Ledger document, so the customer id is `customer_id`
  // (its own `_id` is the ledger id, not the customer's).
  const customerId = customer?.customer_id || order?.Customerid;
  const { outstandingBalance, loading: ledgerLoading } = useLedger(customerId);
  const { clientId, updateEmail } = useCustomer(customerId);
  const { user } = useUser();
  const { sendInvoiceEmail } = useInvoiceEmail();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(true);
  const [email, setEmail] = useState(order?.CustomerEmail || "");
  const [updatedEmail, setUpdatedEmail] = useState(order?.CustomerEmail);
  const [emailError, setEmailError] = useState("");

  const printData = usePrintData(order, outstandingBalance, clientId, user?.organization_arabic_name || "", user?.business_location || "", customer);

  useEffect(() => {
    setEmail(order?.CustomerEmail || "");
  }, [order]);

  const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSendMailClick = () => {
    if (!email) {
      setShowEmailModal(true);
      return;
    }
    sendInvoiceEmail(email, order)
      .then((res) => {
        setTimeout(() => {
          onFinish();
        }, 2000);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleEmailUpdate = async () => {
    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError("Enter a valid email address");
      return;
    }
    try {
      const customer = order.Customer || {};
      const customerId = customer._id?.$oid || order.Customerid;

      const payload = {
        Name: customer.Name || order.CustomerName || "",
        phone_number: customer.phone_number || order.CustomerPhoneNo || "",
        alternativenumber: customer.alternativenumber || "",
        Email: email,
        Address: {
          customeraddress1: customer.Address?.customeraddress1 || order.Address?.customeraddress1 || "",
          customeraddress2: customer.Address?.customeraddress2 || order.Address?.customeraddress2 || "",
          customeraddress3: customer.Address?.customeraddress3 || order.Address?.customeraddress3 || "",
        },
      };
      updateEmail(customerId, payload)
        .then((res) => {
          notifySuccess("Email updated successfully!");
          setUpdatedEmail(email);
          setShowEmailModal(false);
          setTimeout(() => {
            setShowSuccess(false);
            onFinish();
          }, 3000);
          sendInvoiceEmail(email, order);
        })
        .catch((error) => {
          errorHandling(error);
        });
    } catch (error: any) {
      const backendMsg = error?.response?.data?.message;

      notifyError(backendMsg || "Failed to update email.");

      if (backendMsg === "Email already exists") {
        setEmailError("Email already exists.");
        return;
      }

      setEmailError("Failed to update email. Try again.");
    }
  };

  // Don't show the success popup (and its Print button) until the order details
  // have actually been fetched — otherwise the receipt can print with empty
  // data because printData is built before the order GET resolves.
  const isPrintDataReady = !orderLoading && !ledgerLoading && !!order?.Orderid;

  if (showSuccess && !isPrintDataReady) {
    return <LoadingScreen />;
  }

  return (
    <div>
      {showSuccess && (
        <SuccessPopup
          onSendMail={handleSendMailClick}
          onSendMessage={() => console.log("Send SMS")}
          onDone={() => {
            setShowSuccess(false);
            onFinish();
          }}
          printData={printData}
          osb={outstandingBalance}
          disabled={showEmailModal}
        />
      )}

      {showEmailModal && (
        <EmailModal
          email={email}
          emailError={emailError}
          onChange={(e) => {
            setEmail(e);
            setEmailError("");
          }}
          onCancel={() => {
            setShowEmailModal(false);
            setEmailError("");
            setEmail("");
          }}
          onUpdate={handleEmailUpdate}
        />
      )}
    </div>
  );
};

export default Done;
